/**
 * IndexedDB Utility
 * 
 * 統一的 IndexedDB 管理工具
 * 用於存儲路線資訊（字典資料）
 */

const DB_NAME = 'HKTrainDisplayDB';
const DB_VERSION = 1;

// Store 名稱
const STORE_DICTIONARIES = 'dictionaries';
const STORE_METADATA = 'metadata';

let dbInstance = null;

/**
 * 初始化 IndexedDB
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    // 如果已經有實例，直接返回
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      if (process.env.NODE_ENV === 'development') {
        console.error('IndexedDB 打開失敗:', request.error);
      }
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      if (process.env.NODE_ENV === 'development') {
        console.log('[IndexedDB] ✓ 資料庫打開成功:', DB_NAME);
        console.log('[IndexedDB] 資料庫版本:', dbInstance.version);
        console.log('[IndexedDB] Object Stores:', Array.from(dbInstance.objectStoreNames));
      }
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (process.env.NODE_ENV === 'development') {
        console.log('[IndexedDB] 資料庫升級中...', DB_NAME, '版本:', DB_VERSION);
      }

      // 創建字典資料 store
      if (!db.objectStoreNames.contains(STORE_DICTIONARIES)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[IndexedDB] 創建 Object Store:', STORE_DICTIONARIES);
        }
        const dictStore = db.createObjectStore(STORE_DICTIONARIES, { keyPath: 'name' });
        dictStore.createIndex('name', 'name', { unique: true });
        dictStore.createIndex('timestamp', 'timestamp', { unique: false });
        if (process.env.NODE_ENV === 'development') {
          console.log('[IndexedDB] ✓ Object Store 創建成功:', STORE_DICTIONARIES);
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[IndexedDB] Object Store 已存在:', STORE_DICTIONARIES);
        }
      }

      // 創建元資料 store（用於存儲版本號等）
      if (!db.objectStoreNames.contains(STORE_METADATA)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[IndexedDB] 創建 Object Store:', STORE_METADATA);
        }
        const metadataStore = db.createObjectStore(STORE_METADATA, { keyPath: 'key' });
        metadataStore.createIndex('key', 'key', { unique: true });
        if (process.env.NODE_ENV === 'development') {
          console.log('[IndexedDB] ✓ Object Store 創建成功:', STORE_METADATA);
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[IndexedDB] Object Store 已存在:', STORE_METADATA);
        }
      }
    };
  });
};

/**
 * 獲取資料庫實例
 */
const getDB = async () => {
  if (!dbInstance) {
    await initDB();
  }
  return dbInstance;
};

/**
 * 獲取字典資料
 * @param {string} dictName - 字典名稱（如 'MTR', 'LRT', 'MTRBus'）
 * @returns {Promise<Object|null>} 字典資料或 null
 */
export const getDictionary = async (dictName) => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DICTIONARIES], 'readonly');
      const store = transaction.objectStore(STORE_DICTIONARIES);
      const request = store.get(dictName);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // 檢查是否過期（30天）
          const now = Date.now();
          const expiryTime = 30 * 24 * 60 * 60 * 1000; // 30天
          
          if (now - result.timestamp > expiryTime) {
            // 資料已過期，刪除並返回 null
            deleteDictionary(dictName).then(() => resolve(null));
          } else {
            resolve(result.data);
          }
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        if (process.env.NODE_ENV === 'development') {
          console.error(`獲取字典 ${dictName} 失敗:`, request.error);
        }
        reject(request.error);
      };
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`獲取字典 ${dictName} 時發生錯誤:`, error);
    }
    return null;
  }
};

/**
 * 設置字典資料
 * @param {string} dictName - 字典名稱
 * @param {Object} data - 字典資料
 * @returns {Promise<boolean>} 是否成功
 */
export const setDictionary = async (dictName, data) => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[IndexedDB] 開始保存字典 ${dictName}...`);
    }
    const db = await getDB();
    
    if (!db) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[IndexedDB] 資料庫實例為 null');
      }
      return false;
    }
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DICTIONARIES], 'readwrite');
      const store = transaction.objectStore(STORE_DICTIONARIES);
      
      // 檢查資料大小
      const dataSize = JSON.stringify(data).length;
      if (process.env.NODE_ENV === 'development') {
        console.log(`[IndexedDB] 字典 ${dictName} 資料大小: ${(dataSize / 1024).toFixed(2)} KB`);
      }
      
      const dictData = {
        name: dictName,
        data: data,
        timestamp: Date.now()
      };

      const request = store.put(dictData);

      request.onsuccess = () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[IndexedDB] ✓ 字典 ${dictName} 已成功保存到 IndexedDB`);
          // 驗證資料是否真的被保存
          const verifyRequest = store.get(dictName);
          verifyRequest.onsuccess = () => {
            if (verifyRequest.result) {
              console.log(`[IndexedDB] ✓ 驗證成功：字典 ${dictName} 已存在於 IndexedDB`);
            } else {
              console.warn(`[IndexedDB] ⚠ 驗證失敗：字典 ${dictName} 未找到`);
            }
          };
        }
        resolve(true);
      };

      request.onerror = (event) => {
        const error = event.target.error;
        if (process.env.NODE_ENV === 'development') {
          console.error(`[IndexedDB] ✗ 保存字典 ${dictName} 失敗:`, error);
          console.error(`[IndexedDB] 錯誤詳情:`, {
            name: error.name,
            message: error.message,
            code: error.code
          });
        }
        reject(error);
      };
      
      transaction.oncomplete = () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[IndexedDB] 交易完成：字典 ${dictName}`);
        }
      };
      
      transaction.onerror = (event) => {
        if (process.env.NODE_ENV === 'development') {
          console.error(`[IndexedDB] 交易失敗：字典 ${dictName}`, event.target.error);
        }
      };
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[IndexedDB] 保存字典 ${dictName} 時發生異常:`, error);
      console.error(`[IndexedDB] 錯誤堆疊:`, error.stack);
    }
    return false;
  }
};

/**
 * 刪除字典資料
 * @param {string} dictName - 字典名稱
 * @returns {Promise<boolean>} 是否成功
 */
export const deleteDictionary = async (dictName) => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DICTIONARIES], 'readwrite');
      const store = transaction.objectStore(STORE_DICTIONARIES);
      const request = store.delete(dictName);

      request.onsuccess = () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`字典 ${dictName} 已從 IndexedDB 刪除`);
        }
        resolve(true);
      };

      request.onerror = () => {
        if (process.env.NODE_ENV === 'development') {
          console.error(`刪除字典 ${dictName} 失敗:`, request.error);
        }
        reject(request.error);
      };
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`刪除字典 ${dictName} 時發生錯誤:`, error);
    }
    return false;
  }
};

/**
 * 獲取所有字典資料
 * @returns {Promise<Object>} 所有字典資料
 */
export const getAllDictionaries = async () => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DICTIONARIES], 'readonly');
      const store = transaction.objectStore(STORE_DICTIONARIES);
      const request = store.getAll();

      request.onsuccess = () => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[IndexedDB] 從 IndexedDB 讀取到 ${request.result.length} 個字典記錄`);
        }
        const dictionaries = {};
        const now = Date.now();
        const expiryTime = 30 * 24 * 60 * 60 * 1000; // 30天

        request.result.forEach(item => {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[IndexedDB] 字典: ${item.name}, 時間戳: ${new Date(item.timestamp).toISOString()}`);
          }
          // 只返回未過期的資料
          if (now - item.timestamp <= expiryTime) {
            dictionaries[item.name] = item.data;
            if (process.env.NODE_ENV === 'development') {
              console.log(`[IndexedDB] ✓ 字典 ${item.name} 未過期，已載入`);
            }
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log(`[IndexedDB] ⚠ 字典 ${item.name} 已過期，跳過`);
            }
          }
        });

        if (process.env.NODE_ENV === 'development') {
          console.log(`[IndexedDB] 總共載入 ${Object.keys(dictionaries).length} 個有效字典`);
        }
        resolve(dictionaries);
      };

      request.onerror = () => {
        if (process.env.NODE_ENV === 'development') {
          console.error('[IndexedDB] 獲取所有字典失敗:', request.error);
        }
        reject(request.error);
      };
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[IndexedDB] 獲取所有字典時發生錯誤:', error);
    }
    return {};
  }
};

/**
 * 獲取元資料
 * @param {string} key - 元資料鍵（如 'version', 'versionMD5'）
 * @returns {Promise<string|null>} 元資料值或 null
 */
export const getMetadata = async (key) => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_METADATA], 'readonly');
      const store = transaction.objectStore(STORE_METADATA);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };

      request.onerror = () => {
        console.error(`獲取元資料 ${key} 失敗:`, request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error(`獲取元資料 ${key} 時發生錯誤:`, error);
    return null;
  }
};

/**
 * 設置元資料
 * @param {string} key - 元資料鍵
 * @param {string} value - 元資料值
 * @returns {Promise<boolean>} 是否成功
 */
export const setMetadata = async (key, value) => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_METADATA], 'readwrite');
      const store = transaction.objectStore(STORE_METADATA);
      
      const metadata = {
        key: key,
        value: value,
        timestamp: Date.now()
      };

      const request = store.put(metadata);

      request.onsuccess = () => {
        console.log(`元資料 ${key} 已保存到 IndexedDB`);
        resolve(true);
      };

      request.onerror = () => {
        console.error(`保存元資料 ${key} 失敗:`, request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error(`保存元資料 ${key} 時發生錯誤:`, error);
    return false;
  }
};

/**
 * 清除所有字典資料
 * @returns {Promise<boolean>} 是否成功
 */
export const clearAllDictionaries = async () => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DICTIONARIES], 'readwrite');
      const store = transaction.objectStore(STORE_DICTIONARIES);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('所有字典資料已從 IndexedDB 清除');
        resolve(true);
      };

      request.onerror = () => {
        console.error('清除所有字典資料失敗:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('清除所有字典資料時發生錯誤:', error);
    return false;
  }
};

/**
 * 清除過期的字典資料
 * @returns {Promise<number>} 刪除的資料數量
 */
export const clearExpiredDictionaries = async () => {
  try {
    const db = await getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DICTIONARIES], 'readwrite');
      const store = transaction.objectStore(STORE_DICTIONARIES);
      const request = store.getAll();

      request.onsuccess = () => {
        const now = Date.now();
        const expiryTime = 30 * 24 * 60 * 60 * 1000; // 30天
        let deletedCount = 0;

        request.result.forEach(item => {
          if (now - item.timestamp > expiryTime) {
            store.delete(item.name);
            deletedCount++;
          }
        });

        console.log(`已清除 ${deletedCount} 個過期的字典資料`);
        resolve(deletedCount);
      };

      request.onerror = () => {
        console.error('清除過期字典資料失敗:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('清除過期字典資料時發生錯誤:', error);
    return 0;
  }
};

/**
 * 從 localStorage 遷移資料到 IndexedDB
 * @returns {Promise<boolean>} 是否成功
 */
export const migrateFromLocalStorage = async () => {
  try {
    const CACHE_KEY_PREFIX = 'dict_';
    const VERSION_KEY = 'dict_version';
    const MIGRATION_FLAG = 'dict_migrated_to_indexeddb';

    // 檢查是否已經遷移過
    const alreadyMigrated = localStorage.getItem(MIGRATION_FLAG);
    if (alreadyMigrated === 'true') {
      console.log('字典資料已經從 localStorage 遷移到 IndexedDB，跳過遷移');
      return true;
    }

    // 遷移版本號（注意：舊的 localStorage 可能沒有 versionMD5）
    const version = localStorage.getItem(VERSION_KEY);
    if (version) {
      await setMetadata('version', version);
      console.log('版本號已從 localStorage 遷移到 IndexedDB');
      // 注意：舊的 localStorage 沒有 versionMD5，所以不遷移
    }

    // 遷移字典資料
    const keys = Object.keys(localStorage);
    let migratedCount = 0;

    for (const key of keys) {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (cached && cached.data) {
            const dictName = key.replace(CACHE_KEY_PREFIX, '');
            await setDictionary(dictName, cached.data);
            migratedCount++;
            // 遷移成功後，從 localStorage 刪除
            localStorage.removeItem(key);
          }
        } catch (error) {
          console.warn(`遷移 ${key} 時發生錯誤:`, error);
        }
      }
    }

    // 遷移版本號後，從 localStorage 刪除
    if (version) {
      localStorage.removeItem(VERSION_KEY);
    }

    // 標記遷移完成
    if (migratedCount > 0 || version) {
      localStorage.setItem(MIGRATION_FLAG, 'true');
      console.log(`已遷移 ${migratedCount} 個字典從 localStorage 到 IndexedDB，並清理了 localStorage`);
    }

    return true;
  } catch (error) {
    console.error('從 localStorage 遷移資料失敗:', error);
    return false;
  }
};

